import { jsPDF } from 'jspdf';
import Logo from '../../../assets/Logo-Negro.png';
import FirmaGerenciaRRHH from '../../../assets/firmaGerenciaRRHH.jpeg';

/**
 * Formatea una fecha YYYY-MM-DD a formato DD/MM/YYYY sin problemas de timezone
 */
const formatearFechaSinTimezone = (fechaString) => {
  if (!fechaString) return '';
  // Si ya viene en formato DD/MM/YYYY, devolverla tal cual
  if (typeof fechaString === 'string' && fechaString.includes('/')) return fechaString;
  // Si es un string YYYY-MM-DD, convertir manualmente
  if (typeof fechaString === 'string' && fechaString.includes('-')) {
    const [year, month, day] = fechaString.split('-');
    return `${day}/${month}/${year}`;
  }
  // Si es un objeto Date, extraer componentes sin timezone
  if (fechaString instanceof Date) {
    const day = String(fechaString.getDate()).padStart(2, '0');
    const month = String(fechaString.getMonth() + 1).padStart(2, '0');
    const year = fechaString.getFullYear();
    return `${day}/${month}/${year}`;
  }
  return '';
};

/**
 * Genera un PDF de informe con formato profesional y formal
 * Basado en el formato estándar de notificaciones de empresa
 */
export const generateInformePDFMejorado = async ({
  informeNumero = '',
  lugarControl = '',
  fecha = '',
  hora = '',
  actuante = '',
  legajoActuante = '',
  motivo = '',
  motivoDetalle = '',
  empleado = '',
  legajo = '',
  nroCoche = '',
  linea = '',
  servicio = '',
  horaNotificacion = '',
  descargoTexto = '',
  fechaRespuesta = '',
  firmaEmpleadoBase64 = '',
  firmaInspectorBase64 = ''
}) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    let y = 15;

    // ==================== ENCABEZADO ESTILO TABLA ====================
    const headerHeight = 20;
    const col1Width = 50;  // Ancho columna logo
    const col3Width = 50;  // Ancho columna código documento
    const col2Width = pageWidth - col1Width - col3Width; // Ancho columna título

    // Dibujar bordes de la tabla del encabezado
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    
    // Borde exterior del encabezado
    doc.rect(0, 0, pageWidth, headerHeight);
    
    // Líneas verticales separadoras
    doc.line(col1Width, 0, col1Width, headerHeight); // Separador logo
    doc.line(pageWidth - col3Width, 0, pageWidth - col3Width, headerHeight); // Separador código

    // Logo - centrado en la primera columna
    const logoImg = new Image();
    logoImg.src = Logo;
    await new Promise(resolve => { logoImg.onload = resolve; });
    const logoWidth = 30;
    const logoHeight = 14;
    const logoPosX = (col1Width - logoWidth) / 2;
    const logoPosY = (headerHeight - logoHeight) / 2;
    doc.addImage(logoImg, 'PNG', logoPosX, logoPosY, logoWidth, logoHeight);

    // Firma de Gerencia/RRHH (sello fijo)
    const firmaGerenciaImg = new Image();
    firmaGerenciaImg.src = FirmaGerenciaRRHH;
    await new Promise(resolve => { firmaGerenciaImg.onload = resolve; });

    // Título - centrado en la segunda columna
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    const tituloX = col1Width + (col2Width / 2);
    const tituloY = headerHeight / 2 + 2;
    doc.text('INFORME DE SERVICIO', tituloX, tituloY, { align: 'center' });

    // Código de documento y versión - en la tercera columna
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const col3X = pageWidth - col3Width + 5;
    let col3Y = 8;
    
    doc.text('F039', col3X, col3Y);
    col3Y += 6;
    doc.text('VERSIÓN 01', col3X, col3Y);
    col3Y += 6;
    doc.text('REVISIÓN 12/05/26', col3X, col3Y);

    // Número de informe debajo del encabezado
    y = headerHeight + 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`N° ${String(informeNumero || '0').padStart(4, '0')}`, pageWidth - marginRight, y, { align: 'right' });

    y += 5;

    // Línea divisoria
    doc.setLineWidth(0.8);
    doc.line(marginLeft, y, pageWidth - marginRight, y);
    y += 8;

    // ==================== DATOS DEL INFORME ====================
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    const fechaFormateada = formatearFechaSinTimezone(fecha);
    
    // Datos en dos columnas
    const col1X = marginLeft;
    const col2X = pageWidth / 2 + 5;
    const lineHeight = 6;

    // Solo mostrar Lugar de Control si existe
    if (lugarControl) {
      doc.setFont('helvetica', 'bold');
      doc.text('Lugar de Control:', col1X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(lugarControl), col1X + 35, y);
    }

    doc.setFont('helvetica', 'bold');
    doc.text('Fecha:', col2X, y);
    doc.setFont('helvetica', 'normal');
    doc.text(fechaFormateada, col2X + 15, y);
    y += lineHeight;

    if (hora) {
      doc.setFont('helvetica', 'bold');
      doc.text('Hora:', col1X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(hora), col1X + 35, y);
    }

    if (linea) {
      doc.setFont('helvetica', 'bold');
      doc.text('Línea:', col2X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(linea), col2X + 15, y);
    }
    y += lineHeight;

    // Solo mostrar Coche N° si existe
    if (nroCoche) {
      doc.setFont('helvetica', 'bold');
      doc.text('Coche N°:', col1X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(nroCoche), col1X + 35, y);
    }

    if (servicio) {
      doc.setFont('helvetica', 'bold');
      doc.text('Servicio:', col2X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(servicio), col2X + 15, y);
    }
    y += lineHeight + 2;

    // ==================== DATOS DEL EMPLEADO ====================
    doc.setLineWidth(0.3);
    doc.line(marginLeft, y, pageWidth - marginRight, y);
    y += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('DATOS DEL EMPLEADO', marginLeft, y);
    y += 6;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Legajo N°:', col1X, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(legajo || ''), col1X + 20, y);

    doc.setFont('helvetica', 'bold');
    doc.text('Nombre:', col2X, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(empleado || ''), col2X + 18, y);
    y += lineHeight + 3;

    // ==================== MOTIVO ====================
    doc.setLineWidth(0.3);
    doc.line(marginLeft, y, pageWidth - marginRight, y);
    y += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('MOTIVO DEL INFORME', marginLeft, y);
    y += 6;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(String(motivo || ''), marginLeft, y);
    y += lineHeight;

    // Detalle del motivo en caja
    doc.setFont('helvetica', 'normal');
    const motivoLines = doc.splitTextToSize(
      String(motivoDetalle || 'Sin detalle registrado'), 
      contentWidth - 6
    );
    const motivoBoxHeight = Math.max(motivoLines.length * 5 + 8, 25);
    
    doc.setLineWidth(0.3);
    doc.rect(marginLeft, y, contentWidth, motivoBoxHeight);
    doc.text(motivoLines, marginLeft + 3, y + 5);
    y += motivoBoxHeight + 5;

    // ==================== NOTIFICACIÓN ====================
    doc.setLineWidth(0.3);
    doc.line(marginLeft, y, pageWidth - marginRight, y);
    y += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('NOTIFICACIÓN', marginLeft, y);
    y += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const notificacionTexto = `Siendo las __________ hs. del día __________, me notifico del presente informe conforme `;
    const notificacionTexto2 = `lo establece el Convenio Colectivo de Trabajo y el Reglamento Interno de la empresa.`;
    doc.text(notificacionTexto, marginLeft, y);
    y += lineHeight;
    doc.text(notificacionTexto2, marginLeft, y);
    y += lineHeight + 8;

    // ==================== DESCARGO DEL EMPLEADO ====================
    doc.setLineWidth(0.3);
    doc.line(marginLeft, y, pageWidth - marginRight, y);
    y += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('DESCARGO DEL EMPLEADO', marginLeft, y);
    y += 6;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.text('(Dispone de 48hs hábiles para realizar su descargo desde la notificación)', marginLeft, y);
    y += 5;

    // Caja de descargo - más grande para aprovechar espacio
    const espacioRestante = pageHeight - y - 60; // Reservar espacio para firmas
    const descargoBoxHeight = Math.max(espacioRestante * 0.6, 50);

    doc.setLineWidth(0.3);
    doc.rect(marginLeft, y, contentWidth, descargoBoxHeight);

    if (descargoTexto && descargoTexto.trim()) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const descargoLines = doc.splitTextToSize(String(descargoTexto), contentWidth - 6);
      doc.text(descargoLines, marginLeft + 3, y + 5);
    } else {
      // Líneas para escribir a mano
      doc.setLineWidth(0.1);
      doc.setDrawColor(180, 180, 180);
      const numLineas = Math.floor(descargoBoxHeight / 6) - 1;
      for (let i = 1; i <= numLineas; i++) {
        const lineY = y + (i * 6);
        doc.line(marginLeft + 2, lineY, pageWidth - marginRight - 2, lineY);
      }
      doc.setDrawColor(0, 0, 0);
    }

    y += descargoBoxHeight + 10;

    // ==================== FIRMAS ====================
    const firmaAreaHeight = 35;
    const firmaWidth = (contentWidth - 10) / 3; // 3 firmas con espaciado

    // Firma Empleado Notificado
    const firma1X = marginLeft;
    if (firmaEmpleadoBase64) {
      try {
        doc.addImage(firmaEmpleadoBase64, 'PNG', firma1X + 5, y, firmaWidth - 10, 20);
      } catch (e) {
        console.warn('Error al cargar firma empleado:', e);
      }
    }
    doc.setLineWidth(0.3);
    doc.line(firma1X, y + firmaAreaHeight - 15, firma1X + firmaWidth, y + firmaAreaHeight - 15);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('FIRMA Y ACLARACIÓN', firma1X + firmaWidth / 2, y + firmaAreaHeight - 10, { align: 'center' });
    doc.text('EMPLEADO NOTIFICADO', firma1X + firmaWidth / 2, y + firmaAreaHeight - 6, { align: 'center' });

    // Firma Inspector
    // Legajo 9066 carga informes pero no cumple rol de inspector/supervisor,
    // por lo que no debe mostrarse su firma ni sus datos en este recuadro.
    const LEGAJO_SIN_FIRMA_INSPECTOR = '9066';
    const ocultarDatosInspector = String(legajoActuante) === LEGAJO_SIN_FIRMA_INSPECTOR;
    const firma2X = marginLeft + firmaWidth + 5;
    if (firmaInspectorBase64 && !ocultarDatosInspector) {
      try {
        doc.addImage(firmaInspectorBase64, 'PNG', firma2X + 5, y, firmaWidth - 10, 15);
      } catch (e) {
        console.warn('Error al cargar firma inspector:', e);
      }
    }
    doc.line(firma2X, y + firmaAreaHeight - 15, firma2X + firmaWidth, y + firmaAreaHeight - 15);

    // Mostrar nombre y legajo del inspector
    doc.setFontSize(7);
    if (actuante && !ocultarDatosInspector) {
      doc.text(String(actuante), firma2X + firmaWidth / 2, y + firmaAreaHeight - 12, { align: 'center' });
    }
    if (legajoActuante && !ocultarDatosInspector) {
      doc.text(`Leg. ${legajoActuante}`, firma2X + firmaWidth / 2, y + firmaAreaHeight - 9, { align: 'center' });
    }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('INSPECTOR/SUPERVISOR', firma2X + firmaWidth / 2, y + firmaAreaHeight - 6, { align: 'center' });

    // Firma Gerente/RRHH
    const firma3X = marginLeft + (firmaWidth + 5) * 2;
    try {
      doc.addImage(firmaGerenciaImg, 'JPEG', firma3X + 5, y, firmaWidth - 10, 20);
    } catch (e) {
      console.warn('Error al cargar firma gerencia:', e);
    }
    doc.line(firma3X, y + firmaAreaHeight - 15, firma3X + firmaWidth, y + firmaAreaHeight - 15);
    doc.text('FIRMA Y ACLARACIÓN', firma3X + firmaWidth / 2, y + firmaAreaHeight - 10, { align: 'center' });
    doc.text('GERENCIA / RRHH', firma3X + firmaWidth / 2, y + firmaAreaHeight - 6, { align: 'center' });

    // ==================== GENERAR PDF ====================
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');

    return { blob: pdfBlob, url: pdfUrl };
  } catch (error) {
    console.error('Error al generar el PDF del informe:', error);
    throw error;
  }
};
